<template>
  <div
    v-if="toolTip === ''"
    :data-intercom-target="dataTarget"
    class="flex items-center justify-center h-10 p-[10px] cursor-pointer text-sm font-medium rounded-[4px] shadow-imp"
    :class="[`!${customClass}`, variant, { disabled: disabled }]"
    @click="onClick"
  >
    <span
      v-if="leadingIcon"
      class="inline-flex items-center gap-2 px-2 justify-center"
    >
      <font-awesome-icon :icon="leadingIcon" />
    </span>
    <div :class="{ invisible: loading }">
      <slot />
    </div>
    <div
      v-if="loading"
      class="absolute w-5 h-5 rounded-full border-[3px] border-alpha-w50 border-t-white animate-custom-spin"
    ></div>
  </div>
  <!-- kept the tooltip version separate because it breaks tooltips that wraps this component. -->
  <el-tooltip
    v-else
    :content="toolTip"
    :placement="'top'"
    :popper-class="'font-sans'"
    :show-after="500"
  >
    <div
      :data-intercom-target="dataTarget"
      class="relative flex items-center justify-center h-10 p-[10px] rounded-[4px] p-3 text-sm font-medium cursor-pointer"
      :class="[`!${customClass}`, variant, { disabled: disabled }]"
      @click="onClick"
      @click.stop
    >
      <div :class="{ invisible: loading }">
        <slot />
      </div>
      <div
        v-if="loading"
        class="absolute w-5 h-5 rounded-full border-[3px] border-alpha-w50 border-t-white animate-custom-spin"
      ></div>
    </div>
  </el-tooltip>
</template>
<script setup lang="ts">
const emit = defineEmits(["onClick"]);

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: "primary",
  },
  customClass: {
    type: String,
    default: "",
  },
  toolTip: {
    type: String,
    default: "",
  },
  dataTarget: {
    type: String,
    default: "",
  },
  leadingIcon: {
    type: Array as unknown as () => [string, string], // e.g. ['fas', 'plus']
    default: null,
  },
});

const onClick = () => {
  if (!props.disabled && !props.loading) {
    emit("onClick");
  }
};
</script>
<style scoped>
.primary-strong {
  @apply border-[rgba(17,24,39,0.10)] bg-secondary-700 shadow-primary font-subtitle-2 text-white hover:bg-secondary-800;
}
.primary {
  @apply border-[rgba(17,24,39,0.10)] bg-secondary-700 shadow-primary font-subtitle-2 text-white hover:!bg-secondary-800;
}
.primary.disabled {
  @apply bg-accent-100 text-accent-400 hover:!bg-accent-100 cursor-not-allowed;
}

.primary-disabled {
  @apply bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200;
}

.delete {
  @apply bg-danger-600 text-white;
}

.delete.disabled {
  @apply bg-accent-100 text-accent-400 cursor-not-allowed;
}

.secondary {
  @apply border border-accent-200 bg-white hover:border-accent-200 font-subtitle-2 text-accent-700 hover:bg-accent-100 shadow-primary;
}

.secondary.disabled {
  @apply bg-accent-100 text-accent-400 cursor-not-allowed;
}

.outline {
  @apply bg-white outline-none border border-secondary-700 text-secondary-700 shadow-primary font-body-1 hover:bg-secondary-100 hover:border-secondary-100;
}

.outline-secondary-neutral {
  @apply bg-white outline-none font-medium border border-neutral-200 hover:bg-neutral-100 text-gray-600;
}

.outline-secondary {
  @apply bg-white outline-none font-medium border border-secondary-200 hover:bg-secondary-100 text-secondary-700;
}

.rounded {
  @apply cursor-pointer bg-transparent w-10 h-10 rounded-full m-2 hover:bg-accent-100;
}

.dark {
  @apply bg-primary-700 hover:bg-primary-800;
}

.light {
  @apply border border-accent-200 bg-white hover:bg-gray-100;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-custom-spin {
  animation: spin 2s linear infinite;
}
</style>
